/**
 * Builds the props handed to the Intercom Messenger `boot` call.
 *
 * This lives outside the provider because it decides who Intercom thinks the visitor is, and
 * that decision is worth testing on its own rather than through a rendered component.
 */

/** The subset of the authenticated user this needs. Structural on purpose, so `utils` does not
 * depend on the authentication library. */
export type IntercomBootUser = {
  id: string;
  email?: string;
  name?: string;
  picture?: string;
  phone_number?: string;
  inserted_at?: string;
  /**
   * Signed by core-services with the Messenger API secret. `null` until that secret is
   * provisioned: /me always sends the key and nulls it when it cannot sign.
   */
  intercom_user_jwt?: string | null;
};

export type IntercomBootProps = {
  phone?: string;
  avatar: { type: string; imageUrl?: string };
  createdAt?: string;
  name?: string;
  hideDefaultLauncher: boolean;
  userId?: string;
  email?: string;
  customAttributes?: { intercom_user_jwt: string };
};

/**
 * Identity is sent one way or the other, never both.
 *
 * With a JWT, the id and the email travel inside the signed token and nowhere else: Intercom
 * trusts the claims it can verify, and it flags an attribute as "unprotected" when the same
 * value also arrives in the clear next to the token.
 *
 * Without a JWT (the Messenger secret is not provisioned yet) we keep sending the plain id and
 * email, which is the behaviour that has always been there. Dropping them instead would turn
 * every logged-in customer into an anonymous visitor.
 */
export const buildIntercomBootProps = (user: IntercomBootUser, isEmployerApp: boolean): IntercomBootProps => ({
  phone: user.phone_number,
  avatar: { type: 'avatar', imageUrl: user.picture },
  createdAt: user.inserted_at,
  name: user.name,
  hideDefaultLauncher: isEmployerApp,
  ...(user.intercom_user_jwt
    ? { customAttributes: { intercom_user_jwt: user.intercom_user_jwt } }
    : { userId: user.id, email: user.email }),
});
